import React, { useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Country, City } from 'country-state-city';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from '../ui/command';
import {
  User, Mail, Phone, Globe, MapPin, Lock, Eye, EyeOff,
  Sparkles, ChevronsUpDown, Check, ShieldCheck, Instagram
} from 'lucide-react';
import Auth3DShowcase from './Auth3DShowcase';
import PricingPlans from './PricingPlans';
import './login.css';

/**
 * Public self-service signup — captures Full Name / Mobile (country-code dropdown +
 * number) / Email / Country (dropdown of every country) / City (dropdown scoped
 * to the chosen country) / Password; creates the user with a 15-day free demo
 * window and auto-logs them in.
 */
export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromState = location.state?.from;
  const fromQuery = new URLSearchParams(location.search).get('next');
  const redirectTo = fromState || fromQuery || '/';

  const countries = useMemo(() => Country.getAllCountries(), []);

  const [form, setForm] = useState({
    name: '',
    countryCode: '+91',         // dial code (separate field)
    mobileNumber: '',            // digits only
    email: '',
    countryIso: 'IN',            // ISO alpha-2 of selected country (drives city list)
    countryName: 'India',
    city: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [emailTaken, setEmailTaken] = useState(false); // shows the rich "already registered" card
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Cities for currently selected country. Safe even if the country has none.
  const cityOptions = useMemo(() => {
    if (!form.countryIso) return [];
    const list = City.getCitiesOfCountry(form.countryIso) || [];
    // Dedupe by name (some ISO datasets have duplicates across sub-regions).
    const seen = new Set();
    return list.filter(c => {
      if (seen.has(c.name)) return false;
      seen.add(c.name);
      return true;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [form.countryIso]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const digits = form.mobileNumber.replace(/[^0-9]/g, '');
    if (!form.name.trim()) { setError('Please enter your full name.'); return; }
    if (!form.countryCode) { setError('Please choose your mobile country code.'); return; }
    if (digits.length < 6 || digits.length > 14) { setError('Mobile number must be 6–14 digits (without country code).'); return; }
    if (!/\S+@\S+\.\S+/.test(form.email)) { setError('Please enter a valid email.'); return; }
    if (!form.countryName) { setError('Please choose your country.'); return; }
    if (!form.city.trim()) { setError('Please choose your city.'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }

    setLoading(true);
    try {
      // Infer which app the user signed up from based on the redirect-back
      // target (passed via location.state.from or ?next= when the user was
      // bounced here from a tool that needs auth). Admin panel uses this
      // to show "Signed up from: Simulation / P&B / AVD / Homepage".
      const fromPath = (fromState || fromQuery || '').toLowerCase();
      let source = 'homepage';
      if (fromPath.startsWith('/tools/test-pattern')) source = 'tpg';
      else if (fromPath.startsWith('/tools/av-designer')) source = 'avd';
      else if (fromPath.startsWith('/app')) source = 'simulation';

      await signup({
        name: form.name,
        mobile: `${form.countryCode} ${digits}`,
        email: form.email,
        country: form.countryName,
        city: form.city,
        password: form.password,
        source,
      });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const msg = err.message || 'Signup failed';
      // Backend returns 400 with this exact phrase when the email is taken.
      // Detect it (case-insensitive, partial) so we can show the friendly
      // "already registered" card instead of a raw red banner.
      if (/already exists/i.test(msg) || /email.*taken/i.test(msg)) {
        setEmailTaken(true);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="h-[100dvh] w-full max-w-full bg-black grid grid-cols-1 lg:grid-cols-[1.1fr_minmax(460px,0.9fr)] overflow-hidden"
      data-testid="signup-page"
    >
      {/* ── LEFT : 3D cinematic showcase (shared with /login) ── */}
      <Auth3DShowcase
        headline={<>Start your <span className="auth-glass-title">15-day free demo.</span></>}
        subline="No credit card. Full access to projection, LED, truss & simulation tools for 15 days."
      />

      {/* ── RIGHT : Signup glass card.
            `items-start` (not center) so the form starts at the top and the
            extra Plans block at the bottom scrolls cleanly into view, instead
            of being clipped because the centred pane can't grow past the
            viewport. */}
      <div
        className="relative flex items-start justify-center p-6 sm:p-10 auth-grain overflow-y-auto"
        style={{ background: 'radial-gradient(ellipse at center, #0a0d1c 0%, #05060f 60%, #000 100%)' }}
      >
        {/* Soft accent glows behind the card */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-fuchsia-600/10 blur-3xl pointer-events-none" />
        <div className="absolute left-1/3 top-1/3 w-[360px] h-[360px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

        <div className="relative w-full max-w-md auth-fade-up py-4">
          {/* Brand heading */}
          <div className="flex flex-col items-center mb-6">
            <Link
              to="/"
              data-testid="signup-brand-link"
              className="flex items-center gap-3 mb-2 select-none no-underline group"
              aria-label="AV Simulation Studio — home"
            >
              <img
                src="/branding/avst-logo.png"
                alt=""
                className="h-12 w-auto select-none drop-shadow-[0_0_18px_rgba(56,189,248,0.35)] transition-transform group-hover:scale-105"
                draggable={false}
              />
              <span className="text-white font-bold tracking-tight text-xl">AV Simulation Studio</span>
            </Link>
            <p className="text-[11px] text-zinc-500 tracking-[0.28em] uppercase">
              Powered by Ideation Audio Visual
            </p>
          </div>

          {/* Demo banner */}
          <div
            className="mb-4 rounded-xl overflow-hidden border border-fuchsia-500/30 bg-gradient-to-r from-fuchsia-600/10 via-violet-600/8 to-cyan-600/10 backdrop-blur"
            data-testid="signup-demo-banner"
          >
            <div className="flex items-center gap-3 p-3">
              <div className="h-9 w-9 rounded-full bg-fuchsia-500/20 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-fuchsia-300" />
              </div>
              <div>
                <p className="text-fuchsia-100 text-sm font-semibold leading-tight">
                  Sign up and get 15 days free demo
                </p>
                <p className="text-zinc-400 text-[11px]">No payment required. Start exploring today.</p>
              </div>
            </div>
          </div>

          {/* Glass card */}
          <div className="auth-glass rounded-2xl p-6 sm:p-7">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-white">Create your account</h2>
              <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-cyan-300/80">
                <ShieldCheck className="w-3 h-3" /> Secure
              </span>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg p-3 mb-4" data-testid="signup-error">
                {error}
              </div>
            )}

            {emailTaken ? (
              <div className="text-center py-2" data-testid="signup-email-taken">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/15 border border-amber-500/30 mb-3">
                  <ShieldCheck className="w-5 h-5 text-amber-300" />
                </div>
                <h3 className="text-white font-semibold text-base mb-1">This email is already registered</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-5">
                  <span className="text-zinc-200">{form.email}</span> already has an AV Simulation Studio account.
                  Sign in to continue, reset your password, or upgrade to a paid plan.
                </p>
                <div className="space-y-2">
                  <Link to="/login">
                    <Button className="w-full auth-submit h-11 rounded-xl text-white font-semibold" data-testid="signup-go-login-btn">
                      Sign In to Existing Account
                    </Button>
                  </Link>
                  <Link to="/purchase">
                    <Button
                      variant="outline"
                      className="w-full h-11 rounded-xl border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-200 hover:border-fuchsia-300 hover:bg-fuchsia-500/15 transition-colors font-semibold"
                      data-testid="signup-go-purchase-btn"
                    >
                      Purchase a Plan
                    </Button>
                  </Link>
                  <div className="flex items-center justify-center gap-3 pt-1 text-xs">
                    <Link to="/forgot-password" className="text-zinc-400 hover:text-cyan-300 transition-colors" data-testid="signup-forgot-link">
                      Forgot password?
                    </Link>
                    <span className="text-zinc-700">•</span>
                    <button
                      type="button"
                      onClick={() => { setEmailTaken(false); set('email', ''); }}
                      className="text-zinc-400 hover:text-cyan-300 transition-colors"
                      data-testid="signup-try-different-email"
                    >
                      Try a different email
                    </button>
                  </div>
                </div>
              </div>
            ) : (
          <form onSubmit={handleSubmit} className="space-y-3" noValidate>
            {/* Full Name */}
            <FieldWithIcon label="Full Name" required icon={<User className="w-4 h-4" />}>
              <Input
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Your full name"
                className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                data-testid="signup-name-input"
                autoFocus
              />
            </FieldWithIcon>

            {/* Mobile — country-code dropdown + number */}
            <div>
              <Label className="text-zinc-300 text-xs flex items-center gap-1">
                Mobile Number <span className="text-fuchsia-400">*</span>
                <span className="text-zinc-500 ml-auto text-[10px]">Select code, then number</span>
              </Label>
              <div className="flex gap-2 mt-1">
                {/* Country code combobox */}
                <CountryCodeCombobox
                  countries={countries}
                  value={form.countryCode}
                  onSelect={(code) => set('countryCode', code)}
                />
                {/* Number-only input */}
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="tel"
                    value={form.mobileNumber}
                    onChange={(e) => set('mobileNumber', e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="9876543210"
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    data-testid="signup-mobile-input"
                  />
                </div>
              </div>
            </div>

            {/* Email */}
            <FieldWithIcon label="Email ID" required icon={<Mail className="w-4 h-4" />}>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="you@example.com"
                className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                data-testid="signup-email-input"
              />
            </FieldWithIcon>

            {/* Country + City dropdowns */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-zinc-300 text-xs flex items-center gap-1">
                  Country <span className="text-fuchsia-400">*</span>
                </Label>
                <CountryCombobox
                  countries={countries}
                  valueIso={form.countryIso}
                  onSelect={(iso, name) => {
                    setForm(prev => ({ ...prev, countryIso: iso, countryName: name, city: '' }));
                  }}
                />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs flex items-center gap-1">
                  City <span className="text-fuchsia-400">*</span>
                </Label>
                <CityCombobox
                  cities={cityOptions}
                  value={form.city}
                  onSelect={(name) => set('city', name)}
                  disabled={!form.countryIso}
                />
              </div>
            </div>

            {/* Password */}
            <FieldWithIcon label="Password" required hint="At least 6 characters" icon={<Lock className="w-4 h-4" />}>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                placeholder="Create a password"
                className="pl-10 pr-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                data-testid="signup-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </FieldWithIcon>

            {/* Mandatory Terms & Conditions consent */}
            <label
              className="flex items-start gap-2.5 mt-1 text-xs text-zinc-300 cursor-pointer select-none"
              data-testid="signup-terms-row"
            >
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-cyan-500 cursor-pointer shrink-0"
                data-testid="signup-terms-checkbox"
              />
              <span>
                I agree to the{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
                  data-testid="signup-terms-link"
                >
                  Terms &amp; Conditions
                </Link>
                {' '}and acknowledge that AV Simulation Tool is browser-based and I am responsible for
                backing up my own project data.
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading || !acceptedTerms}
              className="auth-submit w-full h-11 rounded-xl text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              data-testid="signup-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating account...
                </span>
              ) : (
                'Start 15-Day Free Demo'
              )}
            </Button>
          </form>
            )}

          <div className="mt-4 text-center text-sm text-zinc-400">
            Already have an account?{' '}
            <Link to="/login" className="text-cyan-300 hover:text-cyan-200 transition-colors" data-testid="signup-to-login-link">
              Sign in
            </Link>
            <span className="mx-2 text-zinc-700">•</span>
            <Link to="/purchase" className="text-fuchsia-300 hover:text-fuchsia-200 transition-colors" data-testid="signup-to-purchase-link">
              Purchase a plan
            </Link>
          </div>
        </div>

        {/* Plans preview — every signup gets a 15-day free demo, then upgrade
            to Pro Monthly ($12) or Pro Yearly ($120, save 17%). Compact mode
            so the cards stay light below the form; CTAs hidden because the
            primary action on this page is the form itself. */}
        <div className="mt-6 auth-glass rounded-2xl p-5" data-testid="signup-plans-preview">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-white font-semibold text-sm">After your free demo</h3>
              <p className="text-zinc-500 text-[11px]">15 days free for every account · upgrade any time</p>
            </div>
            <Link to="/purchase" className="text-[11px] text-cyan-300 hover:text-cyan-200 transition-colors tracking-wider whitespace-nowrap"
                  data-testid="signup-plans-see-all">
              See full plans →
            </Link>
          </div>
          <PricingPlans compact showAllCtas={false} />
        </div>

        {/* Footer: contact + copyright */}
        <div className="mt-6 text-center space-y-3">
          <div className="flex items-center justify-center gap-4 text-[11px] text-zinc-500">
            <a href="https://www.ideationav.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-cyan-300 transition-colors">
              <Globe className="w-3 h-3" /> www.ideationav.com
            </a>
            <span className="text-zinc-700">•</span>
            <a href="mailto:admin@ideationav.com" className="inline-flex items-center gap-1.5 hover:text-cyan-300 transition-colors">
              <Mail className="w-3 h-3" /> admin@ideationav.com
            </a>
            <span className="text-zinc-700">•</span>
            <a
              href="https://www.instagram.com/avsimulationtool/"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram @avsimulationtool"
              className="inline-flex items-center gap-1.5 hover:text-fuchsia-300 transition-colors"
              data-testid="signup-footer-instagram"
            >
              <Instagram className="w-3 h-3" /> @avsimulationtool
            </a>
          </div>
          <p className="text-[10px] text-zinc-600 leading-relaxed">
            © 2026 Ideation Audio Visual. All rights reserved.
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────── helpers ───────────────── */

const FieldWithIcon = ({ label, icon, required, hint, children }) => (
  <div>
    <Label className="text-zinc-300 text-xs flex items-center gap-1">
      {label} {required && <span className="text-fuchsia-400">*</span>}
      {hint && <span className="text-zinc-500 ml-auto text-[10px]">{hint}</span>}
    </Label>
    <div className="relative mt-1">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">{icon}</span>
      {children}
    </div>
  </div>
);

/** Searchable country-dial-code combobox. Value is a string like "+91". */
const CountryCodeCombobox = ({ countries, value, onSelect }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[110px] bg-zinc-800 border-zinc-700 text-white justify-between h-10 px-3"
          data-testid="signup-country-code-btn"
        >
          <span className="text-sm">{value || '+--'}</span>
          <ChevronsUpDown className="w-3 h-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0 bg-zinc-900 border-zinc-700" align="start">
        <Command className="bg-zinc-900">
          <CommandInput placeholder="Search country code..." className="text-white" />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {countries.map(c => {
                const code = c.phonecode?.startsWith('+') ? c.phonecode : `+${c.phonecode}`;
                if (!c.phonecode) return null;
                const label = `${c.flag || ''} ${c.name} (${code})`;
                return (
                  <CommandItem
                    key={c.isoCode + code}
                    value={`${c.name} ${code}`}
                    onSelect={() => { onSelect(code); setOpen(false); }}
                    className="text-zinc-200 data-[selected=true]:bg-fuchsia-500/20"
                    data-testid={`country-code-opt-${c.isoCode}`}
                  >
                    <Check className={`mr-2 w-3 h-3 ${value === code ? 'opacity-100 text-fuchsia-400' : 'opacity-0'}`} />
                    <span className="truncate text-xs">{label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/** Full country name picker. Emits (isoCode, name). */
const CountryCombobox = ({ countries, valueIso, onSelect }) => {
  const [open, setOpen] = useState(false);
  const current = countries.find(c => c.isoCode === valueIso);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full bg-zinc-800 border-zinc-700 text-white justify-between h-10 pl-10 relative"
          data-testid="signup-country-btn"
        >
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <span className="truncate text-sm">
            {current ? `${current.flag || ''} ${current.name}` : 'Select country'}
          </span>
          <ChevronsUpDown className="w-3 h-3 opacity-60 shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 bg-zinc-900 border-zinc-700" align="start">
        <Command className="bg-zinc-900">
          <CommandInput placeholder="Search country..." className="text-white" />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {countries.map(c => (
                <CommandItem
                  key={c.isoCode}
                  value={c.name}
                  onSelect={() => { onSelect(c.isoCode, c.name); setOpen(false); }}
                  className="text-zinc-200 data-[selected=true]:bg-fuchsia-500/20"
                  data-testid={`country-opt-${c.isoCode}`}
                >
                  <Check className={`mr-2 w-3 h-3 ${valueIso === c.isoCode ? 'opacity-100 text-fuchsia-400' : 'opacity-0'}`} />
                  <span className="truncate text-xs">{c.flag || ''} {c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/** City picker scoped to the chosen country. Disabled until a country is set.
 * Supports free-typed city as fallback for countries with empty dataset. */
const CityCombobox = ({ cities, value, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setTyped(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full bg-zinc-800 border-zinc-700 text-white justify-between h-10 pl-10 relative disabled:opacity-50"
          data-testid="signup-city-btn"
        >
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <span className="truncate text-sm">
            {value || (disabled ? 'Pick country first' : 'Select city')}
          </span>
          <ChevronsUpDown className="w-3 h-3 opacity-60 shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 bg-zinc-900 border-zinc-700" align="start">
        <Command className="bg-zinc-900" shouldFilter={true}>
          <CommandInput
            placeholder="Search or type city..."
            className="text-white"
            value={typed}
            onValueChange={setTyped}
          />
          <CommandList>
            <CommandEmpty className="p-2 text-xs text-zinc-400">
              {typed ? (
                <button
                  type="button"
                  className="w-full text-left hover:bg-fuchsia-500/10 p-2 rounded"
                  onClick={() => { onSelect(typed.trim()); setOpen(false); }}
                  data-testid="city-use-typed"
                >
                  Use "<span className="text-fuchsia-300">{typed.trim()}</span>"
                </button>
              ) : (
                <span>No cities found for this country.</span>
              )}
            </CommandEmpty>
            <CommandGroup>
              {cities.slice(0, 1500).map(c => (
                <CommandItem
                  key={c.name}
                  value={c.name}
                  onSelect={() => { onSelect(c.name); setOpen(false); }}
                  className="text-zinc-200 data-[selected=true]:bg-fuchsia-500/20"
                  data-testid={`city-opt-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <Check className={`mr-2 w-3 h-3 ${value === c.name ? 'opacity-100 text-fuchsia-400' : 'opacity-0'}`} />
                  <span className="truncate text-xs">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
